/**
 * DeadlineRuleList コンポーネント
 * FL-110: 取引先締切ルール管理機能 - 一覧表示
 */

import React from 'react';
import Button from '../ui/Button';
import Toggle from '../ui/Toggle';
import { DeadlineRule, DeadlineRuleType, DayOfWeek } from '../../types';

interface DeadlineRuleListProps {
  rules: DeadlineRule[];
  onEdit?: (rule: DeadlineRule) => void;
  onDelete?: (rule: DeadlineRule) => void;
  onToggleActive?: (rule: DeadlineRule) => void;
  isLoading?: boolean;
  readOnly?: boolean;
}

export const DeadlineRuleList: React.FC<DeadlineRuleListProps> = ({
  rules,
  onEdit,
  onDelete,
  onToggleActive,
  isLoading = false,
  readOnly = false,
}) => {
  // 曜日ラベル生成
  const getDayOfWeekLabel = (day: DayOfWeek): string => {
    const labels = {
      [DayOfWeek.SUNDAY]: '日曜日',
      [DayOfWeek.MONDAY]: '月曜日',
      [DayOfWeek.TUESDAY]: '火曜日',
      [DayOfWeek.WEDNESDAY]: '水曜日',
      [DayOfWeek.THURSDAY]: '木曜日',
      [DayOfWeek.FRIDAY]: '金曜日',
      [DayOfWeek.SATURDAY]: '土曜日',
    };
    return labels[day];
  };

  // 曜日順（月曜日から日曜日）の配列
  const weekdayOrder = [
    DayOfWeek.MONDAY,
    DayOfWeek.TUESDAY,
    DayOfWeek.WEDNESDAY,
    DayOfWeek.THURSDAY,
    DayOfWeek.FRIDAY,
    DayOfWeek.SATURDAY,
    DayOfWeek.SUNDAY,
  ];

  // ルールを曜日ごとにグループ化
  const groupedRules = weekdayOrder.reduce(
    (acc, dayOfWeek) => {
      const dayRules = rules.filter(rule => rule.dayOfWeek === dayOfWeek);
      if (dayRules.length > 0) {
        const pickupRule = dayRules.find(rule => rule.ruleType === DeadlineRuleType.PICKUP);
        const deliveryRule = dayRules.find(rule => rule.ruleType === DeadlineRuleType.DELIVERY);

        acc.push({
          dayOfWeek,
          pickupRule,
          deliveryRule,
        });
      }
      return acc;
    },
    [] as Array<{
      dayOfWeek: DayOfWeek;
      pickupRule?: DeadlineRule;
      deliveryRule?: DeadlineRule;
    }>
  );

  // 空の状態
  if (rules.length === 0) {
    return (
      <div className='text-center py-8'>
        <div className='text-gray-500 dark:text-gray-400'>
          <p className='text-lg mb-2'>締切ルールが設定されていません</p>
          <p className='text-sm'>新しい締切ルールを追加してください</p>
        </div>
      </div>
    );
  }

  // 空のグループがある場合の処理
  if (groupedRules.length === 0) {
    return (
      <div className='text-center py-8'>
        <div className='text-gray-500 dark:text-gray-400'>
          <p className='text-lg mb-2'>締切ルールが設定されていません</p>
          <p className='text-sm'>新しい締切ルールを追加してください</p>
        </div>
      </div>
    );
  }

  const renderRuleInfo = (
    rule: DeadlineRule | undefined,
    ruleTypeName: string,
    colorClass: string
  ) => {
    if (!rule) {
      return (
        <div className='flex items-center justify-between py-2'>
          <span className={`text-sm font-medium ${colorClass}`}>{ruleTypeName}</span>
          <span className='text-sm text-gray-500 dark:text-gray-400'>未設定</span>
        </div>
      );
    }

    return (
      <div className='flex items-center justify-between py-2'>
        <div className='flex items-center gap-2'>
          <span className={`text-sm font-medium ${colorClass}`}>{ruleTypeName}</span>
          <span
            className={`text-xs px-2 py-1 rounded-full ${
              rule.isActive
                ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
            }`}
          >
            {rule.isActive ? '有効' : '無効'}
          </span>
        </div>
        <div className='flex items-center gap-2'>
          <span className='text-lg font-mono text-gray-900 dark:text-white'>
            {rule.deadlineTime}
          </span>
          {!readOnly && (
            <>
              <Toggle
                checked={rule.isActive}
                onChange={() => onToggleActive?.(rule)}
                disabled={isLoading}
                size='sm'
              />
              <Button
                variant='ghost'
                size='sm'
                onClick={() => onEdit?.(rule)}
                disabled={isLoading}
                className='text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 text-xs'
              >
                編集
              </Button>
              <Button
                variant='ghost'
                size='sm'
                onClick={() => onDelete?.(rule)}
                disabled={isLoading}
                className='text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 text-xs'
              >
                削除
              </Button>
            </>
          )}
        </div>
      </div>
    );
  };

  // readOnlyモードではコンパクトなテーブル表示
  if (readOnly) {
    return (
      <div
        aria-label='締切ルール一覧'
        className='bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700'
      >
        <div className='overflow-x-auto'>
          <table className='w-full text-sm'>
            <thead className='bg-gray-50 dark:bg-gray-700'>
              <tr>
                <th className='px-3 py-2 text-left font-medium text-gray-900 dark:text-white'>
                  曜日
                </th>
                <th className='px-3 py-2 text-left font-medium text-gray-900 dark:text-white'>
                  搬出締切
                </th>
                <th className='px-3 py-2 text-left font-medium text-gray-900 dark:text-white'>
                  搬入締切
                </th>
              </tr>
            </thead>
            <tbody className='divide-y divide-gray-200 dark:divide-gray-700'>
              {groupedRules.map(({ dayOfWeek, pickupRule, deliveryRule }) => (
                <tr key={dayOfWeek}>
                  <td className='px-3 py-2 font-medium text-gray-900 dark:text-white'>
                    {getDayOfWeekLabel(dayOfWeek)}
                  </td>
                  <td className='px-3 py-2 text-gray-900 dark:text-white font-mono'>
                    {pickupRule ? pickupRule.deadlineTime : '未設定'}
                  </td>
                  <td className='px-3 py-2 text-gray-900 dark:text-white font-mono'>
                    {deliveryRule ? deliveryRule.deadlineTime : '未設定'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // 通常モード（編集可能）
  return (
    <div aria-label='締切ルール一覧' className='space-y-4'>
      {groupedRules.map(({ dayOfWeek, pickupRule, deliveryRule }) => (
        <div
          key={dayOfWeek}
          data-testid='weekday-rule-group'
          className='bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4'
        >
          <div className='space-y-1'>
            {/* 曜日見出し */}
            <div className='mb-3 pb-2 border-b border-gray-200 dark:border-gray-700'>
              <h4 className='text-base font-medium text-gray-900 dark:text-white'>
                {getDayOfWeekLabel(dayOfWeek)}
              </h4>
            </div>

            {/* 搬出締切 */}
            {renderRuleInfo(pickupRule, '搬出締切:', 'text-emerald-600 dark:text-emerald-300')}

            {/* 搬入締切 */}
            {renderRuleInfo(deliveryRule, '搬入締切:', 'text-blue-600 dark:text-blue-300')}

            {/* 備考がある場合の表示 */}
            {(pickupRule?.notes || deliveryRule?.notes) && (
              <div className='pt-2 border-t border-gray-100 dark:border-gray-700/50'>
                {pickupRule?.notes && (
                  <div className='text-xs text-gray-600 dark:text-gray-300 mb-1'>
                    搬出: {pickupRule.notes}
                  </div>
                )}
                {deliveryRule?.notes && (
                  <div className='text-xs text-gray-600 dark:text-gray-300'>
                    搬入: {deliveryRule.notes}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};
